import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { createRestaurant, createCategory, createDish, updateRestaurant, uploadImage, uploadModel, getFileUrl } from '../lib/api';
import { toast } from 'sonner';
import { 
    ChefHat, Store, Utensils, Box, Eye, QrCode, ArrowRight, ArrowLeft, 
    Check, Upload, X, ImageIcon, Loader2, Sparkles, Copy, ExternalLink
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useDropzone } from 'react-dropzone';

const STEPS = [
    { id: 1, title: 'Create Restaurant', icon: Store, description: 'Name and brand your restaurant' },
    { id: 2, title: 'Add Dishes', icon: Utensils, description: 'Add your menu items' },
    { id: 3, title: 'Upload 3D Models', icon: Box, description: 'Make dishes come alive in AR' },
    { id: 4, title: 'Preview Menu', icon: Eye, description: 'See how customers will view it' },
    { id: 5, title: 'Publish & Share', icon: QrCode, description: 'Get your QR code' },
];

export default function SetupWizard() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Restaurant data
    const [restaurant, setRestaurant] = useState(null);
    const [restaurantForm, setRestaurantForm] = useState({
        name: '',
        description: '',
        primary_color: '#C9A227',
        background_color: '#1A1A1A'
    });
    
    // Dishes data
    const [dishes, setDishes] = useState([
        { name: '', description: '', price: '', image: null, imagePreview: null }
    ]);
    
    // Category
    const [category, setCategory] = useState(null);

    const progress = (currentStep / STEPS.length) * 100;

    // Step 1: Create Restaurant
    const handleCreateRestaurant = async () => {
        if (!restaurantForm.name.trim()) {
            toast.error('Please enter a restaurant name');
            return;
        }
        setLoading(true);
        try {
            const response = await createRestaurant({
                ...restaurantForm,
                status: 'draft'
            });
            setRestaurant(response.data);
            
            // Create default category
            const catResponse = await createCategory(response.data.id, {
                name: 'Main Dishes',
                description: 'Our signature dishes',
                sort_order: 0
            });
            setCategory(catResponse.data);
            
            toast.success('Restaurant created!');
            setCurrentStep(2);
        } catch (err) {
            toast.error('Failed to create restaurant');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Add Dishes
    const addDishField = () => {
        setDishes([...dishes, { name: '', description: '', price: '', image: null, imagePreview: null }]);
    };

    const removeDishField = (index) => {
        if (dishes.length > 1) {
            setDishes(dishes.filter((_, i) => i !== index));
        }
    };

    const updateDish = (index, field, value) => {
        const updated = [...dishes];
        updated[index][field] = value;
        setDishes(updated);
    };

    const handleDishImage = async (index, file) => {
        if (!file) return;
        
        // Show preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
            const updated = [...dishes];
            updated[index].imagePreview = e.target.result;
            setDishes(updated);
        };
        reader.readAsDataURL(file);
        
        // Upload
        try {
            const response = await uploadImage(file);
            const updated = [...dishes];
            updated[index].image = getFileUrl(response.data.path);
            setDishes(updated);
            toast.success('Image uploaded!');
        } catch (err) {
            toast.error('Failed to upload image');
        }
    };

    const handleSaveDishes = async () => {
        const validDishes = dishes.filter(d => d.name.trim() && d.price);
        if (validDishes.length === 0) {
            toast.error('Please add at least one dish with name and price');
            return;
        }
        
        setLoading(true);
        try {
            for (const dish of validDishes) {
                await createDish(restaurant.id, {
                    name: dish.name,
                    description: dish.description,
                    price: parseFloat(dish.price),
                    category_id: category.id,
                    image_url: dish.image || '',
                    model_url: dish.model || ''
                });
            }
            toast.success(`${validDishes.length} dishes added!`);
            setCurrentStep(3);
        } catch (err) {
            toast.error('Failed to save dishes');
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Upload 3D Models
    const handleModelUpload = async (index, file) => {
        if (!file) return;
        
        const updated = [...dishes];
        updated[index].modelLoading = true;
        setDishes(updated);
        
        try {
            const response = await uploadModel(file);
            updated[index].model = getFileUrl(response.data.path);
            updated[index].modelLoading = false;
            updated[index].modelUploaded = true;
            setDishes(updated);
            toast.success('3D model uploaded!');
        } catch (err) {
            updated[index].modelLoading = false;
            updated[index].modelError = true;
            setDishes(updated);
            toast.error('Failed to upload 3D model');
        }
    };

    const skipModels = () => {
        setCurrentStep(4);
    };

    const continueWithModels = () => {
        setCurrentStep(4);
    };

    // Step 4: Preview
    const menuUrl = restaurant ? `${window.location.origin}/menu/${restaurant.slug}` : '';

    // Step 5: Publish
    const handlePublish = async () => {
        setLoading(true);
        try {
            await updateRestaurant(restaurant.id, { status: 'published' });
            setRestaurant({ ...restaurant, status: 'published' });
            toast.success('Menu published successfully!');
        } catch (err) {
            toast.error('Failed to publish menu');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(menuUrl);
        toast.success('Link copied to clipboard!');
    };

    const downloadQR = () => {
        const canvas = document.querySelector('#wizard-qr canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${restaurant?.slug || 'menu'}-qr.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const finishWizard = () => {
        navigate(`/dashboard/restaurants/${restaurant.id}`);
    };

    // Dropzone for images
    const ImageDropzone = ({ index }) => {
        const onDrop = useCallback((acceptedFiles) => {
            if (acceptedFiles[0]) {
                handleDishImage(index, acceptedFiles[0]);
            }
        }, [index, handleDishImage]);

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
            maxFiles: 1
        });

        return (
            <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
                    isDragActive ? 'border-[#C9A227] bg-[#C9A227]/10' : 'border-[#E5E1D1] hover:border-[#C9A227]'
                }`}
            >
                <input {...getInputProps()} />
                {dishes[index].imagePreview ? (
                    <div className="relative">
                        <img src={dishes[index].imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                        <button
                            onClick={(e) => { e.stopPropagation(); updateDish(index, 'imagePreview', null); updateDish(index, 'image', null); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ) : (
                    <div className="py-4">
                        <ImageIcon className="w-8 h-8 mx-auto text-[#969282] mb-2" />
                        <p className="text-sm text-[#69665B]">
                            {isDragActive ? 'Drop image here' : 'Drag & drop or click'}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    // Dropzone for 3D models
    const ModelDropzone = ({ index }) => {
        const onDrop = useCallback((acceptedFiles) => {
            if (acceptedFiles[0]) {
                handleModelUpload(index, acceptedFiles[0]);
            }
        }, [index, handleModelUpload]);

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            accept: { 'model/gltf-binary': ['.glb'] },
            maxFiles: 1
        });

        const dish = dishes[index];

        return (
            <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    isDragActive ? 'border-[#C9A227] bg-[#C9A227]/10' : 
                    dish.modelUploaded ? 'border-green-500 bg-green-50' :
                    dish.modelError ? 'border-red-500 bg-red-50' :
                    'border-[#E5E1D1] hover:border-[#C9A227]'
                }`}
            >
                <input {...getInputProps()} />
                {dish.modelLoading ? (
                    <div className="py-4">
                        <Loader2 className="w-8 h-8 mx-auto text-[#C9A227] animate-spin mb-2" />
                        <p className="text-sm text-[#69665B]">Uploading 3D model...</p>
                    </div>
                ) : dish.modelUploaded ? (
                    <div className="py-4">
                        <Check className="w-8 h-8 mx-auto text-green-500 mb-2" />
                        <p className="text-sm text-green-600 font-medium">3D Model uploaded!</p>
                        <p className="text-xs text-[#969282] mt-1">Drop another to replace</p>
                    </div>
                ) : dish.modelError ? (
                    <div className="py-4">
                        <X className="w-8 h-8 mx-auto text-red-500 mb-2" />
                        <p className="text-sm text-red-600">Upload failed. Try again.</p>
                    </div>
                ) : (
                    <div className="py-4">
                        <Box className="w-8 h-8 mx-auto text-[#969282] mb-2" />
                        <p className="text-sm text-[#69665B]">
                            {isDragActive ? 'Drop .glb file here' : 'Drag & drop .glb file'}
                        </p>
                        <p className="text-xs text-[#969282] mt-1">or click to browse</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F5F3E9]">
            {/* Header */}
            <header className="bg-white border-b border-[#E5E1D1] px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#C9A227] rounded-xl flex items-center justify-center">
                            <ChefHat className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-heading text-xl font-medium text-[#2B2A27]">AR Menu Studio</span>
                    </div>
                    <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#69665B]">
                        Exit Setup
                    </Button>
                </div>
            </header>

            {/* Progress */}
            <div className="bg-white border-b border-[#E5E1D1] px-6 py-4">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-3">
                        {STEPS.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = currentStep === step.id;
                            const isCompleted = currentStep > step.id;
                            return (
                                <div key={step.id} className="flex items-center">
                                    <div className={`flex items-center gap-2 ${index > 0 ? 'ml-4' : ''}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                            isCompleted ? 'bg-green-500 text-white' :
                                            isActive ? 'bg-[#C9A227] text-white' :
                                            'bg-[#E5E1D1] text-[#969282]'
                                        }`}>
                                            {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                                        </div>
                                        <span className={`hidden md:block text-sm ${isActive ? 'text-[#2B2A27] font-medium' : 'text-[#969282]'}`}>
                                            {step.title}
                                        </span>
                                    </div>
                                    {index < STEPS.length - 1 && (
                                        <div className={`w-8 md:w-16 h-0.5 ml-4 ${currentStep > step.id ? 'bg-green-500' : 'bg-[#E5E1D1]'}`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <Progress value={progress} className="h-1" />
                </div>
            </div>

            {/* Content */}
            <main className="px-6 py-8">
                <div className="max-w-2xl mx-auto">
                    {/* Step 1: Create Restaurant */}
                    {currentStep === 1 && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Store className="w-8 h-8 text-[#C9A227]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-[#2B2A27] mb-2">Let's create your restaurant</h1>
                                <p className="text-[#69665B]">Start by giving your restaurant a name and choosing your brand colors.</p>
                            </div>

                            <Card className="border-[#E5E1D1]">
                                <CardContent className="p-6 space-y-6">
                                    <div className="space-y-2">
                                        <Label>Restaurant Name *</Label>
                                        <Input
                                            value={restaurantForm.name}
                                            onChange={(e) => setRestaurantForm({ ...restaurantForm, name: e.target.value })}
                                            placeholder="e.g., The Golden Spoon"
                                            className="border-[#E5E1D1] rounded-xl text-lg py-6"
                                            data-testid="wizard-restaurant-name"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Description (optional)</Label>
                                        <Textarea
                                            value={restaurantForm.description}
                                            onChange={(e) => setRestaurantForm({ ...restaurantForm, description: e.target.value })}
                                            placeholder="A brief description of your restaurant..."
                                            className="border-[#E5E1D1] rounded-xl"
                                            rows={3}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Primary Color</Label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={restaurantForm.primary_color}
                                                    onChange={(e) => setRestaurantForm({ ...restaurantForm, primary_color: e.target.value })}
                                                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]"
                                                />
                                                <Input
                                                    value={restaurantForm.primary_color}
                                                    onChange={(e) => setRestaurantForm({ ...restaurantForm, primary_color: e.target.value })}
                                                    className="border-[#E5E1D1] rounded-xl flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Background Color</Label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={restaurantForm.background_color}
                                                    onChange={(e) => setRestaurantForm({ ...restaurantForm, background_color: e.target.value })}
                                                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]"
                                                />
                                                <Input
                                                    value={restaurantForm.background_color}
                                                    onChange={(e) => setRestaurantForm({ ...restaurantForm, background_color: e.target.value })}
                                                    className="border-[#E5E1D1] rounded-xl flex-1"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Preview */}
                                    <div className="border border-[#E5E1D1] rounded-xl p-4" style={{ backgroundColor: restaurantForm.background_color }}>
                                        <p className="text-center text-sm mb-2" style={{ color: restaurantForm.primary_color }}>Preview</p>
                                        <p className="text-center text-xl font-heading" style={{ color: restaurantForm.primary_color }}>
                                            {restaurantForm.name || 'Your Restaurant Name'}
                                        </p>
                                    </div>

                                    <Button
                                        onClick={handleCreateRestaurant}
                                        disabled={loading}
                                        className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full py-6 text-lg gap-2"
                                        data-testid="wizard-create-restaurant-btn"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5" /></>}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Step 2: Add Dishes */}
                    {currentStep === 2 && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Utensils className="w-8 h-8 text-[#C9A227]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-[#2B2A27] mb-2">Add your dishes</h1>
                                <p className="text-[#69665B]">Add the dishes you want to show on your menu. You can add more later.</p>
                            </div>

                            <div className="space-y-4">
                                {dishes.map((dish, index) => (
                                    <Card key={dish.name || `dish-${index}`} className="border-[#E5E1D1]">
                                        <CardContent className="p-4">
                                            <div className="flex items-start gap-4">
                                                <div className="w-32 flex-shrink-0">
                                                    <ImageDropzone index={index} />
                                                </div>
                                                <div className="flex-1 space-y-3">
                                                    <Input
                                                        value={dish.name}
                                                        onChange={(e) => updateDish(index, 'name', e.target.value)}
                                                        placeholder="Dish name *"
                                                        className="border-[#E5E1D1] rounded-xl"
                                                        data-testid={`wizard-dish-name-${index}`}
                                                    />
                                                    <div className="flex gap-2">
                                                        <Input
                                                            value={dish.description}
                                                            onChange={(e) => updateDish(index, 'description', e.target.value)}
                                                            placeholder="Description (optional)"
                                                            className="border-[#E5E1D1] rounded-xl flex-1"
                                                        />
                                                        <Input
                                                            type="number"
                                                            value={dish.price}
                                                            onChange={(e) => updateDish(index, 'price', e.target.value)}
                                                            placeholder="Price *"
                                                            className="border-[#E5E1D1] rounded-xl w-24"
                                                            data-testid={`wizard-dish-price-${index}`}
                                                        />
                                                    </div>
                                                </div>
                                                {dishes.length > 1 && (
                                                    <Button variant="ghost" size="icon" onClick={() => removeDishField(index)} className="text-red-500">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}

                                <Button variant="outline" onClick={addDishField} className="w-full border-dashed border-[#C9A227] text-[#C9A227] rounded-xl">
                                    + Add another dish
                                </Button>

                                <div className="flex gap-3 pt-4">
                                    <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1 rounded-full py-6">
                                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                                    </Button>
                                    <Button
                                        onClick={handleSaveDishes}
                                        disabled={loading}
                                        className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full py-6"
                                        data-testid="wizard-save-dishes-btn"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Upload 3D Models */}
                    {currentStep === 3 && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Box className="w-8 h-8 text-[#C9A227]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-[#2B2A27] mb-2">Add 3D models (optional)</h1>
                                <p className="text-[#69665B]">Upload .glb files to let customers view dishes in AR. You can skip this step.</p>
                            </div>

                            <div className="space-y-4">
                                {dishes.filter(d => d.name).map((dish, index) => (
                                    <Card key={dish.name} className="border-[#E5E1D1]">
                                        <CardContent className="p-4">
                                            <div className="flex items-center gap-4 mb-3">
                                                {dish.imagePreview && (
                                                    <img src={dish.imagePreview} alt={dish.name} className="w-12 h-12 object-cover rounded-lg" />
                                                )}
                                                <div>
                                                    <p className="font-medium text-[#2B2A27]">{dish.name}</p>
                                                    <p className="text-sm text-[#969282]">${dish.price}</p>
                                                </div>
                                            </div>
                                            <ModelDropzone index={index} />
                                        </CardContent>
                                    </Card>
                                ))}

                                <div className="flex gap-3 pt-4">
                                    <Button variant="outline" onClick={skipModels} className="flex-1 rounded-full py-6">
                                        Skip for now
                                    </Button>
                                    <Button
                                        onClick={continueWithModels}
                                        className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full py-6"
                                    >
                                        Continue <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Preview */}
                    {currentStep === 4 && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Eye className="w-8 h-8 text-[#C9A227]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-[#2B2A27] mb-2">Preview your menu</h1>
                                <p className="text-[#69665B]">This is how your customers will see your menu on their phones.</p>
                            </div>

                            <div className="flex justify-center mb-6">
                                <div className="w-[300px] h-[600px] bg-black rounded-[40px] p-3 shadow-2xl">
                                    <iframe
                                        src={menuUrl}
                                        className="w-full h-full rounded-[32px] bg-white"
                                        title="Menu Preview"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-center mb-4">
                                <Button variant="outline" onClick={() => window.open(menuUrl, '_blank')} className="gap-2">
                                    <ExternalLink className="w-4 h-4" /> Open in new tab
                                </Button>
                            </div>

                            <div className="flex gap-3">
                                <Button variant="outline" onClick={() => setCurrentStep(3)} className="flex-1 rounded-full py-6">
                                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                                </Button>
                                <Button
                                    onClick={() => setCurrentStep(5)}
                                    className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full py-6"
                                >
                                    Looks great! <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Publish */}
                    {currentStep === 5 && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Sparkles className="w-8 h-8 text-[#C9A227]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-[#2B2A27] mb-2">
                                    {restaurant?.status === 'published' ? 'Your menu is live!' : 'Ready to publish?'}
                                </h1>
                                <p className="text-[#69665B]">
                                    {restaurant?.status === 'published' 
                                        ? 'Share your menu with customers using the QR code below.'
                                        : 'Publish your menu to make it accessible to customers.'}
                                </p>
                            </div>

                            {restaurant?.status !== 'published' ? (
                                <Card className="border-[#E5E1D1] mb-6">
                                    <CardContent className="p-6 text-center">
                                        <Button
                                            onClick={handlePublish}
                                            disabled={loading}
                                            className="bg-green-600 hover:bg-green-700 text-white rounded-full py-6 px-12 text-lg gap-2"
                                            data-testid="wizard-publish-btn"
                                        >
                                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                                                <Check className="w-5 h-5" /> Publish Menu
                                            </>}
                                        </Button>
                                    </CardContent>
                                </Card>
                            ) : (
                                <>
                                    <Card className="border-[#E5E1D1] mb-6" id="wizard-qr">
                                        <CardContent className="p-8 text-center">
                                            <div className="inline-block bg-white p-6 rounded-2xl shadow-lg mb-4">
                                                <QRCodeCanvas
                                                    value={menuUrl}
                                                    size={200}
                                                    level="H"
                                                    fgColor={restaurant?.primary_color || '#C9A227'}
                                                />
                                            </div>
                                            <p className="text-[#2B2A27] font-medium mb-1">{restaurant?.name}</p>
                                            <p className="text-sm text-[#969282] mb-4 break-all">{menuUrl}</p>
                                            
                                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                                <Button onClick={copyLink} variant="outline" className="gap-2 rounded-full" data-testid="copy-link-btn">
                                                    <Copy className="w-4 h-4" /> Copy Link
                                                </Button>
                                                <Button onClick={downloadQR} className="bg-[#C9A227] hover:bg-[#B8922A] text-white gap-2 rounded-full" data-testid="download-qr-btn">
                                                    <QrCode className="w-4 h-4" /> Download QR
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Button
                                        onClick={finishWizard}
                                        className="w-full bg-[#2B2A27] hover:bg-[#1a1a1a] text-white rounded-full py-6 text-lg"
                                        data-testid="wizard-finish-btn"
                                    >
                                        Go to Dashboard
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
